import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getSafeErrorMessage } from '../utils/security';
import { log } from '../utils/logger';

export const AuthContext = createContext();

const SESSION_COOKIE_NAMES = ['nv_session', 'mm_session'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;

  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get('/auth/verify');
      if (response.data.valid) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Se for erro 401 (não autorizado) ou 403 (proibido), limpar usuário
      // Isso significa que o token é inválido ou expirado
      if (error.response?.status === 401 || error.response?.status === 403) {
        setUser(null);
        // Limpar cookies manualmente também
        const cookieNames = [...SESSION_COOKIE_NAMES, 'csrf_token'];
        cookieNames.forEach(name => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          if (cookieDomain) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${cookieDomain};`;
          }
        });
        // Não redirecionar se já estiver na página de login (evita loop)
        const currentPath = window.location.pathname;
        if (currentPath !== '/admin' && currentPath !== '/cadastro') {
          // Não fazer redirect automático aqui, deixar o interceptor do axios fazer se necessário
        }
      } else if (error.response?.status === 500) {
        // Se for erro 500, pode ser problema temporário do servidor
        // Não alterar user para evitar perda de sessão por problemas temporários
        // O estado atual será mantido
      } else {
        // Outros erros (rede, etc) - manter estado atual
        // Não alterar user para evitar perda de sessão por problemas de rede
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user } = response.data;
      setUser(user);
      return { success: true, user };
    } catch (error) {
      const data = error.response?.data;
      return {
        success: false,
        error: getSafeErrorMessage(error, 'Erro ao fazer login'),
        code: typeof data?.code === 'string' ? data.code : undefined,
        needsVerification: data?.needsVerification === true
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      await api.post('/auth/register', {
        username,
        email,
        password
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: getSafeErrorMessage(error, 'Erro ao cadastrar')
      };
    }
  };

  const logout = async () => {
    try {
      // Aguardar o logout no servidor para garantir que os cookies sejam limpos
      await api.post('/auth/logout');
    } catch (error) {
      // Ignorar erros, mas continuar com limpeza local
      log.warn('Erro no logout (continuando limpeza local)');
    }
    
    // Limpar estado local
    setUser(null);
    
    // Limpar cookies manualmente no cliente também (fallback)
    // Isso garante que mesmo se o servidor não conseguir limpar, o cliente limpa
    const cookieNames = [...SESSION_COOKIE_NAMES, 'csrf_token'];
    cookieNames.forEach(name => {
      // Limpar para o domínio atual
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      if (cookieDomain) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${cookieDomain};`;
      }
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, verifyToken }}>
      {children}
    </AuthContext.Provider>
  );
};

